import { useTranslation } from '@rhino-project/core/i18n';
import React, { useCallback, useState } from 'react';
import { Button, TextArea, TextField } from '@heroui/react';
import { getModel } from '@rhino-project/core/utils';
import { modelKey, useModelCreate } from '@rhino-project/core/hooks';
import { useQueryClient } from '@tanstack/react-query';

interface InlineNoteFormProps {
  playerId: unknown;
  onSave?: () => void;
}

// Compact note composer used by the player pages. Grows from one row to three
// on focus and submits on Cmd/Ctrl+Enter.
const InlineNoteForm = ({ playerId, onSave }: InlineNoteFormProps) => {
  const { t } = useTranslation();

  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState(1);
  const { mutate: createNote } = useModelCreate('note');
  const queryClient = useQueryClient();

  const handleSubmit = useCallback(() => {
    const text = note.trim();
    if (!text) return;
    setSaving(true);
    createNote(
      { player: playerId, note: text } as unknown as Parameters<
        typeof createNote
      >[0],
      {
        onSuccess: () => {
          setNote('');
          queryClient.invalidateQueries({
            queryKey: [modelKey(getModel('note'), 'index')]
          });
          onSave?.();
        },
        onSettled: () => setSaving(false)
      }
    );
  }, [note, playerId, createNote, queryClient, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div className="flex gap-2 items-start">
      <TextField
        value={note}
        onChange={setNote}
        className="flex-1"
        aria-label={t('frontend.messages.note')}
      >
        <TextArea
          placeholder={t('frontend.messages.add_a_note')}
          rows={rows}
          onFocus={() => setRows(3)}
          onBlur={() => {
            if (!note) setRows(1);
          }}
          onKeyDown={handleKeyDown}
          className="resize-none"
        />
      </TextField>
      {note.trim() && (
        <Button
          variant="primary"
          size="sm"
          onPress={handleSubmit}
          isDisabled={saving}
        >
          {saving
            ? t('frontend.messages.saving_2')
            : t('frontend.messages.save')}
        </Button>
      )}
    </div>
  );
};

export default InlineNoteForm;
